import type { NewsRelationId } from '@vue-storefront/craftcms-api';
import { computed, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';
import { useSdk } from '~/sdk';

interface UseBlogOptions {
  termId?: MaybeRefOrGetter<NewsRelationId | null | undefined>;
  authorId?: MaybeRefOrGetter<NewsRelationId | null | undefined>;
}

const BLOG_PAGE_SIZE = 9;
const GRAPHQL_INT_MAX = 2_147_483_647;
const MAX_BLOG_PAGE = Math.floor(GRAPHQL_INT_MAX / BLOG_PAGE_SIZE) + 1;

const createCmsError = (cause: unknown) => createError({
  statusCode: 502,
  statusMessage: 'CMS request failed',
  message: 'Unable to load blog content',
  cause,
  fatal: true,
});

const createBlogPageError = () => createError({
  statusCode: 404,
  message: 'Blog page not found',
  fatal: true,
});

/**
 * Fail the initial SSR/navigation request and surface failures from later
 * reactive useAsyncData refreshes through Nuxt's error page.
 */
export function handleBlogCmsError(error: Readonly<Ref<unknown>>) {
  if (error.value) {
    throw createCmsError(error.value);
  }

  watch(error, (cause) => {
    if (cause) {
      showError(createCmsError(cause));
    }
  });
}

export async function useBlog(options: UseBlogOptions = {}) {
  const route = useRoute();
  const { locale } = useI18n();

  const requestedPage = computed(() => {
    const rawPage = Array.isArray(route.query.page) ? route.query.page[0] : route.query.page;
    if (rawPage == null) return 1;
    if (typeof rawPage !== 'string' || !/^[1-9]\d*$/.test(rawPage)) return null;

    const page = Number(rawPage);
    return Number.isSafeInteger(page) && page <= MAX_BLOG_PAGE ? page : null;
  });
  const currentPage = computed(() => requestedPage.value ?? 1);

  if (requestedPage.value === null) {
    throw createBlogPageError();
  }

  watch(requestedPage, (page) => {
    if (page === null) showError(createBlogPageError());
  });

  const termId = computed(() => toValue(options.termId) ?? undefined);
  const authorId = computed(() => toValue(options.authorId) ?? undefined);
  const stateKey = computed(() => [
    'blog',
    locale.value,
    `page-${currentPage.value}`,
    `term-${termId.value ?? 'all'}`,
    `author-${authorId.value ?? 'all'}`,
  ].join(':'));

  const { data: response, pending, error } = await useAsyncData(
    stateKey,
    () => useSdk().cms.getNews({
      relatedTo: termId.value,
      authorId: authorId.value,
      offset: (currentPage.value - 1) * BLOG_PAGE_SIZE,
      limit: BLOG_PAGE_SIZE,
    }),
  );

  handleBlogCmsError(error);

  const data = computed(() => response.value?.entries ?? []);
  const totalItems = computed(() => response.value?.total ?? 0);
  const getPageCount = (total: number) => Math.max(1, Math.ceil(total / BLOG_PAGE_SIZE));

  if (response.value && currentPage.value > getPageCount(response.value.total)) {
    throw createBlogPageError();
  }

  // Validate only after the response for the new reactive key arrives. During
  // navigation useAsyncData may briefly expose no data while page > 1.
  watch(response, (value) => {
    if (value && currentPage.value > getPageCount(value.total)) {
      showError(createBlogPageError());
    }
  });

  const hasPagination = computed(() => totalItems.value > BLOG_PAGE_SIZE);
  const isFirstPage = computed(() => currentPage.value === 1);

  return {
    data,
    first: computed(() => isFirstPage.value ? (data.value[0] ?? null) : null),
    hasPagination,
    isFirstPage,
    loading: pending,
    currentPage,
    pageSize: BLOG_PAGE_SIZE,
    totalItems,
  };
}
